import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getTravelPlans, deleteTravelPlan as deletePlanAPI, updateTravelPlan as updatePlanAPI } from '../../services/api';

// Async thunk for fetching travel plans
export const fetchTravelPlans = createAsyncThunk(
  'travelPlans/fetchTravelPlans',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { lastFetched, data } = state.travelPlans;
      
      // Cache for 5 minutes - only refetch if data is stale
      const cacheTimeout = 5 * 60 * 1000; // 5 minutes
      const now = Date.now();
      
      if (lastFetched && data.length > 0 && (now - lastFetched) < cacheTimeout) {
        // Return cached data without making API call
        return { data, fromCache: true };
      }
      
      const response = await getTravelPlans();
      return { data: response.data, fromCache: false };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch travel plans');
    }
  }
);

// Async thunk for deleting travel plan
export const deleteTravelPlan = createAsyncThunk(
  'travelPlans/deleteTravelPlan',
  async (planId, { rejectWithValue }) => {
    try {
      await deletePlanAPI(planId);
      return planId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete travel plan');
    }
  }
);

// Async thunk for updating travel plan
export const updateTravelPlan = createAsyncThunk(
  'travelPlans/updateTravelPlan',
  async ({ planId, updates }, { rejectWithValue }) => {
    try {
      const response = await updatePlanAPI(planId, updates);
      return { planId, updates: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update travel plan');
    }
  }
);

const travelPlansSlice = createSlice({
  name: 'travelPlans',
  initialState: {
    data: [],
    filteredPlans: [],
    upcomingPlans: [],
    pastPlansByYear: {},
    availableTags: [],
    availableDestinations: [],
    loading: false,
    error: null,
    lastFetched: null,
    searchQuery: '',
    sortOption: 'startDate-desc',
    tagFilter: [],
    destinationFilter: '',
  },
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setSortOption: (state, action) => {
      state.sortOption = action.payload;
    },
    setTagFilter: (state, action) => {
      state.tagFilter = action.payload;
    },
    setDestinationFilter: (state, action) => {
      state.destinationFilter = action.payload;
    },
    clearFilters: (state) => {
      state.searchQuery = '';
      state.sortOption = 'startDate-desc';
      state.tagFilter = [];
      state.destinationFilter = '';
    },
    setFilteredPlans: (state, action) => {
      state.filteredPlans = action.payload;
    },
    setTimelineGroups: (state, action) => {
      state.upcomingPlans = action.payload.upcoming;
      state.pastPlansByYear = action.payload.past;
    },
    addTravelPlan: (state, action) => {
      state.data.unshift(action.payload); // Add to beginning
      // Update derived data
      const allTags = [...new Set(state.data.flatMap(plan => plan.tags || []))];
      const allDestinations = [...new Set(state.data.map(plan => plan.destination).filter(Boolean))];
      state.availableTags = allTags;
      state.availableDestinations = allDestinations;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch travel plans
      .addCase(fetchTravelPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTravelPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.data = [...action.payload.data].sort((a, b) => {
          const dateA = new Date(a.startDate || 0);
          const dateB = new Date(b.startDate || 0);
          return dateB - dateA;
        });
        
        // Extract unique tags and destinations for filters
        const allTags = [...new Set(state.data.flatMap(plan => plan.tags || []))];
        const allDestinations = [...new Set(state.data.map(plan => plan.destination).filter(Boolean))];
        state.availableTags = allTags;
        state.availableDestinations = allDestinations;
        
        // Update cache timestamp only if data came from API (not cache)
        if (!action.payload.fromCache) {
          state.lastFetched = Date.now();
        }
      })
      .addCase(fetchTravelPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete travel plan
      .addCase(deleteTravelPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTravelPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.data = state.data.filter(plan => (plan.id || plan._id) !== action.payload);
        // Update derived data
        const allTags = [...new Set(state.data.flatMap(plan => plan.tags || []))];
        const allDestinations = [...new Set(state.data.map(plan => plan.destination).filter(Boolean))];
        state.availableTags = allTags;
        state.availableDestinations = allDestinations;
      })
      .addCase(deleteTravelPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update travel plan
      .addCase(updateTravelPlan.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTravelPlan.fulfilled, (state, action) => {
        const { planId, updates } = action.payload;
        const planIndex = state.data.findIndex(plan => (plan.id || plan._id) === planId);
        if (planIndex !== -1) {
          state.data[planIndex] = { ...state.data[planIndex], ...updates };
        }
      })
      .addCase(updateTravelPlan.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  setSearchQuery,
  setSortOption,
  setTagFilter,
  setDestinationFilter,
  clearFilters,
  setFilteredPlans,
  setTimelineGroups,
  addTravelPlan,
  invalidateCache,
  clearError
} = travelPlansSlice.actions;

export default travelPlansSlice.reducer; 